import type { CapitalRepayment, ExpenseRule, RepaymentEffect } from '../types'
import { expandRecurring, expenseAmountOn } from './cashflowEngine'
import { addDays, fromISODate, toISODate } from './dateRules'
import { formatMoney } from './format'
import { makeId } from './id'

export interface AmortizationRow {
  monthIndex: number
  date: string
  openingBalance: number
  interest: number
  principalPaid: number
  closingBalance: number
  /** Capital repaid at the start of this month, before its interest (only present when there was some). */
  repayment?: number
}

export interface AmortizationResult {
  schedule: AmortizationRow[]
  payoffDate: string | null
  monthsToPayoff: number | null
  totalInterestPaid: number
  /** true if the payment doesn't even cover the monthly interest, so it never pays off */
  neverPaysOff: boolean
}

/** A lump sum off the balance on a date, as `amortizeLoan` takes it. */
export interface LumpSum {
  date: string // ISO yyyy-mm-dd
  amount: number
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1)
}

function monthKey(d: Date): string {
  return toISODate(d).slice(0, 7)
}

/**
 * Projects a simple monthly-compounding amortization schedule.
 * This is a planning approximation (interest compounds monthly on the
 * declining balance) — it does not attempt to replicate a specific lender's
 * exact daily-interest or fee calculation.
 *
 * `repayments` (SM-41) are lump sums taken off the balance at the start of the
 * month they fall in, before that month's interest, so interest is lower from
 * then on. Ones before `startDate`'s month are ignored, and one larger than
 * the balance only clears it. Without them the result is as it always was.
 */
export function amortizeLoan(
  principal: number,
  aprPercent: number,
  monthlyPayment: number,
  startDate: Date,
  maxMonths = 600,
  repayments: LumpSum[] = [],
): AmortizationResult {
  const schedule: AmortizationRow[] = []
  let balance = Math.max(principal, 0)
  const monthlyRate = Math.max(aprPercent, 0) / 100 / 12
  let totalInterest = 0

  const startMonth = monthKey(startDate)
  const lumpByMonth = new Map<string, number>()
  for (const r of repayments) {
    const key = r.date.slice(0, 7)
    if (key < startMonth || !(r.amount > 0)) continue
    lumpByMonth.set(key, (lumpByMonth.get(key) ?? 0) + r.amount)
  }
  const lumpIn = (d: Date, opening: number) => Math.min(lumpByMonth.get(monthKey(d)) ?? 0, opening)

  if (balance <= 0) {
    return { schedule, payoffDate: toISODate(startDate), monthsToPayoff: 0, totalInterestPaid: 0, neverPaysOff: false }
  }

  const firstInterest = (balance - lumpIn(addMonths(startDate, 0), balance)) * monthlyRate
  if (monthlyPayment <= firstInterest) {
    return { schedule, payoffDate: null, monthsToPayoff: null, totalInterestPaid: 0, neverPaysOff: true }
  }

  for (let i = 0; i < maxMonths; i++) {
    const date = addMonths(startDate, i)
    const opening = balance
    const repayment = lumpIn(date, opening)
    const owed = opening - repayment
    const interest = owed * monthlyRate
    let principalPaid = owed > 0 ? monthlyPayment - interest : 0
    if (principalPaid > owed) principalPaid = owed
    const closing = Math.max(owed - principalPaid, 0)

    totalInterest += interest
    schedule.push({
      monthIndex: i,
      date: toISODate(date),
      openingBalance: opening,
      interest,
      principalPaid,
      closingBalance: closing,
      ...(repayment > 0 ? { repayment } : {}),
    })
    balance = closing

    if (balance <= 0.005) {
      return {
        schedule,
        payoffDate: toISODate(date),
        monthsToPayoff: i + 1,
        totalInterestPaid: totalInterest,
        neverPaysOff: false,
      }
    }
  }

  return { schedule, payoffDate: null, monthsToPayoff: null, totalInterestPaid: totalInterest, neverPaysOff: false }
}

/** The level monthly payment that clears `balance` in `payments` months at `aprPercent`. */
export function annuityPayment(balance: number, aprPercent: number, payments: number): number {
  if (payments <= 0 || balance <= 0) return 0
  const r = Math.max(aprPercent, 0) / 100 / 12
  if (r === 0) return balance / payments
  return (balance * r) / (1 - Math.pow(1 + r, -payments))
}

/** Capital repayments that count against `principal`: on or after `principalAsOf`, oldest first. */
export function countedRepayments(loan: Pick<ExpenseRule, 'principalAsOf' | 'capitalRepayments'>): CapitalRepayment[] {
  return (loan.capitalRepayments ?? [])
    .filter((r) => !loan.principalAsOf || r.date >= loan.principalAsOf)
    .sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * What is owed on a date: `principal` less the capital repaid since
 * `principalAsOf`, up to and including that day. Regular payments aren't
 * taken off here, as `principal` never modelled them. Undefined when unknown.
 */
export function balanceOn(
  loan: Pick<ExpenseRule, 'principal' | 'principalAsOf' | 'capitalRepayments'>,
  isoDate: string,
): number | undefined {
  if (loan.principal === undefined) return undefined
  const repaid = countedRepayments(loan)
    .filter((r) => r.date <= isoDate)
    .reduce((sum, r) => sum + Math.abs(r.amount), 0)
  return Math.max(roundPence(loan.principal - repaid), 0)
}

/** The loan's payment dates after `isoDate` (not on it), up to its end date or `maxMonths` on. */
export function paymentsAfter(loan: ExpenseRule, isoDate: string, maxMonths = 600): string[] {
  const from = addDays(fromISODate(isoDate), 1)
  const to = new Date(from.getFullYear(), from.getMonth() + maxMonths, from.getDate())
  return expandRecurring(loan, from, to).map(toISODate)
}

/**
 * Why a repayment can't change the loan's term or payment, or null if it can.
 * The repayment can still be recorded; it then only lowers the balance.
 */
export function repaymentEffectBlocker(
  loan: Pick<ExpenseRule, 'frequency' | 'principal' | 'endDate' | 'interestRateApr'>,
  effect: RepaymentEffect,
): string | null {
  if (loan.frequency !== 'monthly') return 'Only for loans paid monthly'
  if (loan.principal === undefined) return 'Needs the outstanding balance'
  if (effect === 'lower') {
    if (!loan.endDate) return 'Needs a final payment date'
    if (loan.interestRateApr === undefined) return 'Needs an interest rate'
  }
  return null
}

export interface RepaymentInput {
  id?: string
  date: string
  amount: number
  effect: RepaymentEffect
  importId?: string
}

/**
 * Records a capital repayment on a loan and applies what the lender does
 * after it, returning the updated rule (the input is left alone). The edit
 * form uses it, and 41b's statement import will for a capital repayment row.
 *
 * - `principalAsOf` is set to the repayment's date if it wasn't set, so
 *   `principal` stays the balance before it.
 * - `shorten` keeps the payment and brings `endDate` earlier by the payments
 *   the lump sum saves (never later than it was; set if there was none).
 * - `lower` keeps `endDate` and, from the next payment, cuts the amount by the
 *   level payment the lump sum would have covered over the payments left. That
 *   equals the annuity over the remaining payments at the new balance, but is
 *   worked from today's payment so a rough `principal` doesn't skew it. Amount
 *   changes from that payment on are replaced.
 *
 * When `repaymentEffectBlocker` gives a reason, only the balance changes.
 */
export function applyCapitalRepayment(loan: ExpenseRule, input: RepaymentInput): ExpenseRule {
  const repayment: CapitalRepayment = {
    id: input.id ?? makeId(),
    date: input.date,
    amount: roundPence(Math.abs(input.amount)),
    effect: input.effect,
    ...(input.importId ? { importId: input.importId } : {}),
  }
  const before: ExpenseRule = { ...loan, principalAsOf: loan.principalAsOf ?? repayment.date }
  const after: ExpenseRule = { ...before, capitalRepayments: [...(loan.capitalRepayments ?? []), repayment] }

  if (repaymentEffectBlocker(loan, repayment.effect)) return after
  const balBefore = balanceOn(before, repayment.date) ?? 0
  const balAfter = balanceOn(after, repayment.date) ?? 0
  const repaid = balBefore - balAfter
  if (repaid <= 0) return after

  const dates = paymentsAfter(loan, repayment.date)
  if (dates.length === 0) return after
  if (balAfter <= 0) return { ...after, endDate: earlier(loan.endDate, repayment.date) }

  const apr = loan.interestRateApr ?? 0
  const next = dates[0]
  const payment = expenseAmountOn(loan, next)

  if (repayment.effect === 'lower') {
    // Rounded up to the penny, so the last payment still clears it.
    const amount = Math.max(Math.ceil((payment - annuityPayment(repaid, apr, dates.length)) * 100 - 1e-6) / 100, 0)
    const changes = (loan.amountChanges ?? []).filter((c) => c.from < next)
    return { ...after, amountChanges: [...changes, { from: next, amount }] }
  }

  const start = fromISODate(next)
  const withLump = amortizeLoan(balAfter, apr, payment, start).monthsToPayoff
  if (withLump === null) return after
  const without = amortizeLoan(balBefore, apr, payment, start).monthsToPayoff

  let end: string
  if (loan.endDate && without !== null) {
    // Take the payments saved off the end date the person gave, rather than
    // trusting the engine's own payoff date for a balance they may have rounded.
    const keep = dates.length - (without - withLump)
    end = keep >= 1 ? dates[keep - 1] : repayment.date
  } else {
    end = dates[Math.min(withLump, dates.length) - 1]
  }
  return { ...after, endDate: earlier(loan.endDate, end) }
}

const monthYear = (iso: string) => fromISODate(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })

/**
 * What recording a repayment did, from the loan before and after
 * `applyCapitalRepayment`: "balance £182,400.00 → £172,400.00; ends Jun 2049
 * (was Mar 2051)" or "…; payment £900.00 → £850.00 from Jul 2027".
 */
export function describeRepaymentEffect(before: ExpenseRule, after: ExpenseRule, repaymentDate: string, symbol = '£'): string {
  const was = balanceOn(before, repaymentDate)
  const now = balanceOn(after, repaymentDate)
  const parts = [was === undefined || now === undefined ? 'balance not known' : `balance ${formatMoney(was, symbol)} → ${formatMoney(now, symbol)}`]

  const newChange = (after.amountChanges ?? []).find((c) => !(before.amountChanges ?? []).some((b) => b.from === c.from && b.amount === c.amount))
  if (newChange) {
    parts.push(`payment ${formatMoney(expenseAmountOn(before, newChange.from), symbol)} → ${formatMoney(newChange.amount, symbol)} from ${monthYear(newChange.from)}`)
  } else if (after.endDate && after.endDate !== before.endDate) {
    parts.push(`ends ${monthYear(after.endDate)} (${before.endDate ? `was ${monthYear(before.endDate)}` : 'no end date before'})`)
  } else {
    parts.push('end date and payment unchanged')
  }
  return parts.join('; ')
}

/** Drops a recorded repayment. Its effect on `endDate` or `amountChanges` is not undone. */
export function removeCapitalRepayment(loan: ExpenseRule, id: string): ExpenseRule {
  return { ...loan, capitalRepayments: (loan.capitalRepayments ?? []).filter((r) => r.id !== id) }
}

function earlier(current: string | undefined, proposed: string): string {
  return current && current < proposed ? current : proposed
}

function roundPence(n: number): number {
  return Math.round(n * 100) / 100
}

// re-export for callers that just want "today + n days" convenience
export { addDays }
